/**
 * AYU Gamification Widget
 * Zeigt Prana-Punkte, Level, Streak, AYU Score und aktive Challenges
 * Design: Warm & natuerlich passend zum AYU-Branding
 */
import { useState } from "react";
import { Link } from "wouter";
import { trpc } from "@/lib/trpc";
import {
  Flame,
  Star,
  Trophy,
  Target,
  ArrowRight,
  Loader2,
  Zap,
  TrendingUp,
  Shield,
  Snowflake,
  ChevronRight,
  Award,
} from "lucide-react";

interface GamificationWidgetProps {
  clientId: string;
}

export default function GamificationWidget({ clientId }: GamificationWidgetProps) {
  const summary = trpc.gamification.summary.useQuery(
    { clientId },
    { staleTime: 60_000, refetchOnWindowFocus: false }
  );

  if (summary.isLoading) {
    return (
      <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-[#6b7c3e]" />
          <span className="ml-2 text-sm text-[#8a9680]">Lade Gamification...</span>
        </div>
      </div>
    );
  }

  if (summary.error || !summary.data) return null;

  const { totalPoints, level, levelProgress, badges, activeChallenges, streaks, ayuScore, freezeStatus } = summary.data;

  // Gesamt-Streak finden
  const gesamtStreak = streaks.find((s: { streakType: string }) => s.streakType === "gesamt");
  const currentStreak = gesamtStreak?.currentStreak ?? 0;

  return (
    <div className="space-y-4">
      {/* Haupt-Widget: Punkte + Level + Score */}
      <div className="rounded-2xl border border-[#d4dcc4] bg-gradient-to-br from-white to-[#f5f3eb] p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: level.color + "20" }}>
              <Star className="w-4 h-4" style={{ color: level.color }} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#2d3a1e]">Dein AYU-Pfad</h3>
              <p className="text-xs text-[#8a9680]">
                Level {level.id}: {level.name}
                <span className="text-[#8b7a5e] ml-1 italic">({level.nameSanskrit})</span>
              </p>
            </div>
          </div>
          <Link href="/gamification">
            <span className="text-xs text-[#6b7c3e] hover:underline cursor-pointer flex items-center gap-1">
              Details <ChevronRight className="w-3 h-3" />
            </span>
          </Link>
        </div>

        {/* Level Progress Bar */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-[#8a9680] mb-1.5">
            <span>{totalPoints.toLocaleString("de-DE")} Prana</span>
            <span>{levelProgress}%</span>
          </div>
          <div className="h-2.5 bg-[#e8e4d8] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000 ease-out"
              style={{
                width: `${levelProgress}%`,
                background: `linear-gradient(90deg, ${level.color}88, ${level.color})`,
              }}
            />
          </div>
          <p className="text-[10px] text-[#8a9680] mt-1">{level.description}</p>
        </div>

        {/* Stats Grid: Streak + Score + Badges */}
        <div className="grid grid-cols-3 gap-3">
          {/* Streak */}
          <div className="bg-white rounded-xl p-3 border border-[#d4dcc4] text-center">
            <Flame className="w-4 h-4 mx-auto mb-1" style={{ color: currentStreak > 0 ? "#E8856C" : "#8a9680" }} />
            <p className="text-xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {currentStreak}
            </p>
            <p className="text-[10px] text-[#8a9680]">Tage-Serie</p>
            {freezeStatus.available > 0 && (
              <div className="flex items-center justify-center gap-0.5 mt-1">
                <Snowflake className="w-2.5 h-2.5 text-blue-400" />
                <span className="text-[9px] text-blue-400">{freezeStatus.available} Freeze</span>
              </div>
            )}
          </div>

          {/* AYU Score */}
          <div className="bg-white rounded-xl p-3 border border-[#d4dcc4] text-center">
            <TrendingUp className="w-4 h-4 mx-auto mb-1 text-[#6b7c3e]" />
            <p className="text-xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {ayuScore?.totalScore ?? "—"}
            </p>
            <p className="text-[10px] text-[#8a9680]">AYU Score</p>
          </div>

          {/* Badges */}
          <Link href="/gamification">
            <div className="bg-white rounded-xl p-3 border border-[#d4dcc4] text-center cursor-pointer hover:bg-[#f0f3eb] transition-colors group">
              <Trophy className="w-4 h-4 mx-auto mb-1 text-[#8b6914]" />
              <p className="text-xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {badges.length}
              </p>
              <p className="text-[10px] text-[#8a9680]">Abzeichen</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Aktive Challenges */}
      {activeChallenges.length > 0 && (
        <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-[#6b7f4e]" />
              <h3 className="text-sm font-medium text-[#6b7f4e]">Wöchentliche Challenges</h3>
            </div>
            <Link href="/gamification">
              <span className="text-xs text-[#6b7c3e] hover:underline cursor-pointer">Alle</span>
            </Link>
          </div>
          <div className="space-y-2.5">
            {activeChallenges.slice(0, 3).map((challenge: {
              id: number;
              title: string;
              currentCount: number;
              targetCount: number;
              rewardPoints: number;
              status: string;
            }) => {
              const progress = Math.min(100, Math.round((challenge.currentCount / challenge.targetCount) * 100));
              const isComplete = challenge.status === "completed";
              return (
                <div key={challenge.id} className={`rounded-lg p-3 border ${isComplete ? "border-[#6b7c3e]/30 bg-[#f0f3eb]" : "border-[#e8e4d8]"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-sm font-medium ${isComplete ? "text-[#6b7c3e]" : "text-[#2d3a1e]"}`}>
                      {isComplete && <span className="mr-1">&#10003;</span>}
                      {challenge.title}
                    </span>
                    <span className="text-xs text-[#8b6914] flex items-center gap-0.5">
                      <Zap className="w-3 h-3" />
                      {challenge.rewardPoints}
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#e8e4d8] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${progress}%`,
                        backgroundColor: isComplete ? "#6b7c3e" : "#8b6914",
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-[#8a9680] mt-1">
                    {challenge.currentCount}/{challenge.targetCount}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
